<template>
  <div class="content-page">
    <div class="container-fluid">
      <!-- Display error message if user data is not found -->
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
        <router-link to="/auth-sign-in" class="alert-link">Sign in again</router-link>
      </div>

      <!-- Show loading state while fetching user data -->
      <div v-else-if="loading" class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading dashboard...</span>
        </div>
      </div>

      <!-- Main content -->
      <div v-else class="row">
        <div class="col-lg-4">
          <div class="card card-transparent card-block card-stretch card-height border-none">
            <div class="card-body p-0 mt-lg-2 mt-0">
              <h3 class="mb-3">Hi {{ user?.first_name || "User" }}, Good Morning</h3>
              <p class="mb-0 mr-4">
                Your dashboard gives you views of key performance or business process
              </p>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div class="row">
            <div class="col-lg-12 col-md-12">
              <div class="card card-block card-stretch card-height">
                <div class="card-body">
                  <div class="d-flex align-items-center mb-4 card-total-sale justify-content-between">
                    <div class="d-flex align-items-center">
                      <div class="icon iq-icon-box-2 bg-info-light">
                        <img src="assets/images/product/1.png" class="img-fluid" alt="image" />
                      </div>
                      <div class="ms-3">
                        <p class="mb-2">
                          {{ membershipFeePaid ? 'Inviting Friends and Get Rewarded Immediately' : 'Pay Membership Fee to Unlock Features' }}
                        </p>
                      </div>
                    </div>
                    <div>
                      <button
      v-if="membershipFeePaid"
      class="btn btn-primary view-btn font-size-14"
      data-toggle="modal"
      data-target="#new-order"
    >
      Refer a Friend
    </button>
    <router-link
      v-else
      to="/membership-fee"
      class="btn btn-primary view-btn font-size-14"
    >
      Pay Membership Fee
    </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <router-view name="dashboardItem"></router-view>
        </div>
        <div class="col-lg-4">
          <div class="card card-transparent card-block card-stretch mb-4">
            <div class="card-header d-flex align-items-center justify-content-between p-0">
              <div class="header-title">
                <h4 class="card-title mb-0">Your Virtual Wallets</h4>
              </div>
              <div class="card-header-toolbar d-flex align-items-center"></div>
            </div>
          </div>
          <div class="card card-block card-stretch card-height-helf">
            <div class="card-body card-item-right wallet-card">
              <div class="d-flex align-items-top">
                <div class="style-text text-left w-100">
                  <div class="wallet-info">
                    <h5 class="card-title">Dalto Investments</h5>
                    <div>
                      <p class="wallet-type mb-0">USDT Wallet Address</p>
                      <p class="wallet-number mb-0">
                        {{ user?.usdt_wallet || "N/A" }}
                      </p>
                    </div>
                  </div>
                  <p class="card-footer">
                    {{ user?.first_name }} {{ user?.last_name }}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="card card-block card-stretch card-height-helf">
            <div class="card-body card-item-right">
              <div class="d-flex align-items-top">
                <div class="bg-danger-light rounded">
                  <img src="assets/images/product/05.png" class="style-img img-fluid m-auto" alt="image" />
                </div>
                <div class="style-text text-left">
                  <p class="mb-2">Your Wallet: {{ walletAmount }} USDT</p>
                  <p class="mb-0">USDT Wallet Address: {{ user?.usdt_wallet || "N/A" }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import apiClient from '../api/axios'

export default defineComponent({
  setup () {
    const router = useRouter()

    // Reactive state for user data, loading, and error
    const user = ref<any>(null)
    const loading = ref(true)
    const error = ref<string | null>(null)
    const loggingOut = ref(false)
    const walletAmount = ref('0.00')
    const membershipFeePaid = ref(false)
    const userId = ref<number | null>(null)

    // Computed properties for routes
    const dashboardRoute = computed(() => {
      if (!user.value) return '/dashboard'
      return user.value.role === 'user_client' ? '/' : '/dashboard'
    })

    const walletRoute = computed(() => {
      if (!user.value) return '/wallet-deposit'
      return user.value.role === 'user_client' ? '/wallet-deposit' : '/wallet-deposit-admin'
    })

    // Fetch user data from localStorage
    const fetchUserData = () => {
      try {
        const userData = localStorage.getItem('user')
        if (!userData) {
          throw new Error('User data not found in localStorage')
        }
        user.value = JSON.parse(userData)
        userId.value = user.value?.id || null
      } catch (err) {
        console.error('Failed to parse user data:', err)
        error.value = 'User data not found. Please sign in to continue'
        user.value = null
        userId.value = null
      }
    }

    // Fetch wallet amount
    const fetchWalletAmount = async () => {
      if (!userId.value) {
        console.error('User ID not available')
        return
      }
      try {
        console.log('Fetching wallet amount for user ID:', userId.value)
        const response = await apiClient.get('/user-wallet-amount/' + userId.value, {
          data: { user_id: userId.value }
        })
        console.log('Wallet amount response:', response.data)
        walletAmount.value = response.data.data.wallet_balance || '0.00'
      } catch (err: any) {
        console.error('Failed to fetch wallet amount:', err.response?.data || err)
        handleError(err)
      }
    }

    // Fetch membership status
    const fetchMembershipStatus = async () => {
      try {
        console.log('Fetching membership status...')
        const response = await apiClient.get('/membership-status')
        console.log('Membership status response:', response.data)
        membershipFeePaid.value = response.data.data.membership_fee_paid
      } catch (err: any) {
        console.error('Failed to fetch membership status:', err.response?.data || err)
        handleError(err)
      }
    }

    // Handle logout
    const handleLogout = async () => {
      loggingOut.value = true
      try {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        router.push('/auth-sign-in')
      } catch (err) {
        console.error('Logout failed:', err)
      } finally {
        loggingOut.value = false
      }
    }

    // Copy promo code to clipboard
    const copyPromoCode = () => {
      const promoCode = user.value?.promocode || 'N/A'
      navigator.clipboard.writeText(promoCode).then(() => {
        alert('Promo code copied to clipboard!')
      }).catch((err) => {
        console.error('Failed to copy promo code:', err)
        alert('Failed to copy promo code.')
      })
    }

    // Handle errors
    const handleError = (err: any) => {
      if (err.response?.status === 401 || err.response?.status === 403) {
        console.log('Unauthorized error - redirecting to sign-in')
        error.value = 'Your session has expired. Please sign in again'
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user')
        router.push('/auth-sign-in')
      } else {
        console.error('Error in handleError:', err.response?.data || err)
      }
    }

    // Lifecycle hook
    onMounted(async () => {
      try {
        fetchUserData()
        const token = localStorage.getItem('auth_token')
        if (!user.value || !token) {
          error.value = 'User data or token not found. Please sign in to continue'
          router.push('/auth-sign-in')
          return
        }

        apiClient.defaults.headers.common.Authorization = `Bearer ${token}`
        await Promise.all([fetchWalletAmount(), fetchMembershipStatus()])
      } catch (err) {
        console.error('Error during mounted:', err)
        error.value = 'Failed to load user data. Please sign in again'
      } finally {
        loading.value = false
      }
    })

    return {
      user,
      loading,
      error,
      loggingOut,
      walletAmount,
      membershipFeePaid,
      dashboardRoute,
      walletRoute,
      handleLogout,
      copyPromoCode
    }
  }
})
</script>

<style scoped>
.wallet-card {
  background: linear-gradient(135deg, #d3b8a0 0%, #a68b6e 100%);
  border-radius: 15px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  min-height: 150px;
}

.wallet-card::before {
  content: "Dalto I";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 50px;
  color: rgba(255, 255, 255, 0.1);
  font-weight: bold;
  pointer-events: none;
}

.wallet-card .style-text {
  position: relative;
  z-index: 1;
  padding: 15px;
}

.wallet-card .wallet-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.wallet-card .card-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin: 0;
}

.wallet-card .wallet-type {
  font-size: 0.75rem;
  opacity: 0.8;
}

.wallet-card .wallet-number {
  font-size: 0.85rem;
  font-weight: bold;
}

.wallet-card .card-footer {
  font-size: 0.9rem;
  font-weight: 500;
  margin: 0 0 10px 0;
}

.wallet-card .wallet-stats p {
  font-size: 0.8rem;
  font-weight: 400;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
}

.alert-link {
  color: #721c24;
  text-decoration: underline;
}

.alert-link:hover {
  color: #50141a;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
